import {
  ApiError,
  DASHBOARD_TILE_IDS,
  type DashboardTileId,
  type Permission,
  queryKeys,
  type StaffDashboardPrefs,
  staffDashboardPrefsSchema,
} from '@mevaser/shared'
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { SlidersHorizontal } from 'lucide-react'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

/**
 * Every tile the dashboard can show, in the order the Customize menu lists
 * them. `permission` is what the tile's own data needs; a person without it
 * never sees the tile or its menu entry. Where each tile sits on the page is
 * decided in the dashboard itself.
 */
export const DASHBOARD_TILES: {
  id: DashboardTileId
  label: string
  permission: Permission | null
}[] = [
  { id: 'awaiting-provisioning', label: 'Awaiting provisioning', permission: 'inventory:read' },
  { id: 'ready-to-sell', label: 'Ready to sell', permission: 'inventory:read' },
  { id: 'low-stock', label: 'Low stock items', permission: 'inventory:read' },
  { id: 'kiosks-offline', label: 'Kiosks offline', permission: 'kiosks:read' },
  { id: 'open-tickets', label: 'Open tickets', permission: 'support:read' },
  { id: 'open-tasks', label: 'Unfinished tasks', permission: 'tasks:read' },
  { id: 'recent-orders', label: 'Recent orders', permission: 'orders:read' },
  // The feed is the signed-in person's own: tickets, missed calls and completed tasks all
  // write to it, so any staff member may have rows.
  { id: 'recent-notifications', label: 'Recent notifications', permission: null },
  { id: 'provisioning-pipeline', label: 'Provisioning pipeline', permission: 'inventory:read' },
  { id: 'staff', label: 'Staff', permission: 'staff:read' },
  { id: 'on-the-clock', label: 'On the clock', permission: 'timeclock:manage' },
  { id: 'signed-in-now', label: 'Signed in now', permission: 'staff:read' },
]

/**
 * The signed-in person's hidden tiles. Until the first answer arrives nothing
 * is visible, so a tile they hid never flashes up (or fires its request); if
 * the request fails the dashboard falls back to showing everything.
 */
export function useDashboardTiles(): { isVisible: (id: DashboardTileId) => boolean } {
  const { can } = useAuth()
  const prefs = useQuery({
    queryKey: queryKeys.dashboardPrefs(),
    queryFn: () =>
      api.get<StaffDashboardPrefs>('/me/dashboard-preferences', {
        schema: staffDashboardPrefsSchema,
      }),
  })
  const ready = prefs.data !== undefined || prefs.isError
  const hidden = new Set(prefs.data?.hiddenTiles ?? [])

  return {
    isVisible: (id) => {
      if (!ready || hidden.has(id)) return false
      const tile = DASHBOARD_TILES.find((t) => t.id === id)
      return tile !== undefined && (tile.permission === null || can(tile.permission))
    },
  }
}

export function DashboardCustomizeMenu() {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const prefs = useQuery({
    queryKey: queryKeys.dashboardPrefs(),
    queryFn: () =>
      api.get<StaffDashboardPrefs>('/me/dashboard-preferences', {
        schema: staffDashboardPrefsSchema,
      }),
  })

  const save = useMutation({
    mutationFn: (hiddenTiles: DashboardTileId[]) =>
      api.patch<StaffDashboardPrefs>(
        '/me/dashboard-preferences',
        { hiddenTiles },
        { schema: staffDashboardPrefsSchema },
      ),
    // Show the change at once so a quick second click builds on it.
    onMutate: (hiddenTiles) => {
      queryClient.setQueryData<StaffDashboardPrefs>(queryKeys.dashboardPrefs(), { hiddenTiles })
    },
    onSuccess: (next) => queryClient.setQueryData(queryKeys.dashboardPrefs(), next),
    onError: (error) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboardPrefs() })
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined)
    },
  })

  const toggle = (id: DashboardTileId, show: boolean) => {
    // Rebuilt from the registry's ids, so a tile retired in a later release
    // drops out of the saved list the next time anyone changes it.
    const current = queryClient.getQueryData<StaffDashboardPrefs>(queryKeys.dashboardPrefs())
    const hidden = new Set((current?.hiddenTiles ?? prefs.data?.hiddenTiles ?? []) as string[])
    if (show) hidden.delete(id)
    else hidden.add(id)
    save.mutate(DASHBOARD_TILE_IDS.filter((tileId) => hidden.has(tileId)))
  }

  const hidden = new Set(prefs.data?.hiddenTiles ?? [])
  const tiles = DASHBOARD_TILES.filter((t) => t.permission === null || can(t.permission))

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" disabled={prefs.data === undefined}>
          <SlidersHorizontal aria-hidden="true" />
          Customize
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="px-2 py-1.5 text-xs font-normal text-foreground-muted">
          Choose what shows on your dashboard
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {tiles.map((tile) => (
          <DropdownMenuCheckboxItem
            key={tile.id}
            checked={!hidden.has(tile.id)}
            onCheckedChange={(checked) => toggle(tile.id, checked === true)}
            // Keep the menu open so several tiles can be switched in one go.
            onSelect={(event) => event.preventDefault()}
          >
            {tile.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
